import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sun, Moon, Menu, X, Check } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useLanguage } from "@/context/LanguageContext";
import { useTheme } from "@/context/ThemeContext";
import { LANGS } from "@/i18n/translations";
import { Link } from "react-router-dom";
import { scrollToId } from "@/components/SmoothScroll";

export const Header = () => {
  const { lang, setLang, t } = useLanguage();
  const { theme, toggle } = useTheme();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "about", label: t("nav.about") },
    { id: "activities", label: t("nav.activities") },
    { id: "library", label: t("nav.library") },
    { id: "contact", label: t("nav.contact") },
  ];

  const go = (id) => {
    setMobileOpen(false);
    scrollToId(id);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1], delay: 0.2 }}
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled
          ? "bg-background/70 dark:bg-background/60 backdrop-blur-xl border-b border-border"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 flex items-center justify-between h-20">
        <button
          onClick={() => go("top")}
          data-testid="logo-home"
          className="flex items-center gap-3 group"
        >
          <span className="grid place-items-center h-12 w-12 sm:h-16 sm:w-16 rounded-full bg-white shadow-md ring-1 ring-black/5 shrink-0">
            <img src="/estesia-emblem.png" alt="Estesia" className="h-11 sm:h-[60px] w-auto object-contain" />
          </span>
          <span className="flex flex-col items-stretch leading-none">
            <span className={`flex justify-between font-serif text-2xl sm:text-3xl font-semibold transition-colors duration-300 ${scrolled ? "text-foreground" : "text-white [text-shadow:0_2px_14px_rgba(0,0,0,0.75)]"}`}>
              {"ESTESIA".split("").map((c, i) => (
                <span key={i}>{c}</span>
              ))}
            </span>
            <span className={`w-full text-center font-sans uppercase text-[8px] sm:text-[10px] font-bold tracking-[0.08em] sm:tracking-[0.14em] mt-1 sm:mt-1.5 whitespace-nowrap transition-colors duration-300 ${scrolled ? "text-primary" : "text-white [text-shadow:0_1px_10px_rgba(0,0,0,0.8)]"}`}>
              {`${t("wordmark.city")}, ${t("wordmark.country")} • ${t("wordmark.since")} 1991`}
            </span>
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <button
              key={l.id}
              onClick={() => go(l.id)}
              data-testid={`nav-${l.id}`}
              className={`font-sans text-sm tracking-wide link-underline pb-1 transition-colors duration-300 ${scrolled ? "text-foreground/80 hover:text-foreground" : "text-white/85 hover:text-white"}`}
            >
              {l.label}
            </button>
          ))}
          <Link
            to="/trabalhadores"
            data-testid="nav-workers"
            className={`font-sans text-sm tracking-wide link-underline pb-1 transition-colors duration-300 ${scrolled ? "text-foreground/80 hover:text-foreground" : "text-white/85 hover:text-white"}`}
          >
            {t("nav.workers")}
          </Link>
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid="lang-switcher"
              className={`flex items-center gap-1.5 font-sans text-xs uppercase tracking-[0.15em] px-3 py-2 rounded-sm border transition-colors duration-300 ${scrolled ? "border-border hover:border-primary text-foreground" : "border-white/40 hover:border-white text-white"}`}
            >
              {LANGS.find((x) => x.code === lang)?.label} <span className={scrolled ? "text-primary" : "text-white/80"}>▾</span>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[180px]">
              {LANGS.map((l) => (
                <DropdownMenuItem
                  key={l.code}
                  data-testid={`lang-option-${l.code}`}
                  onClick={() => setLang(l.code)}
                  className="flex items-center justify-between cursor-pointer"
                >
                  <span>{l.name}</span>
                  {lang === l.code && <Check size={14} className="text-primary" strokeWidth={1.5} />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <button
            onClick={toggle}
            data-testid="theme-toggle"
            aria-label="Toggle theme"
            className={`p-2 rounded-sm border transition-colors duration-300 ${scrolled ? "border-border hover:border-primary text-foreground" : "border-white/40 hover:border-white text-white"}`}
          >
            {theme === "dark" ? <Sun size={16} strokeWidth={1.5} /> : <Moon size={16} strokeWidth={1.5} />}
          </button>

          <button
            onClick={() => setMobileOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
            className={`md:hidden p-2 rounded-sm border transition-colors duration-300 ${scrolled || mobileOpen ? "border-border text-foreground" : "border-white/40 text-white"}`}
          >
            {mobileOpen ? <X size={16} strokeWidth={1.5} /> : <Menu size={16} strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.76, 0, 0.24, 1] }}
            className="md:hidden overflow-hidden bg-background/95 backdrop-blur-xl border-b border-border"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <button
                  key={l.id}
                  onClick={() => go(l.id)}
                  data-testid={`mobile-nav-${l.id}`}
                  className="text-left font-serif text-2xl text-foreground"
                >
                  {l.label}
                </button>
              ))}
              <Link
                to="/trabalhadores"
                onClick={() => setMobileOpen(false)}
                data-testid="mobile-nav-workers"
                className="text-left font-serif text-2xl text-foreground"
              >
                {t("nav.workers")}
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
